/*globals showModalInfo, showModalError*/
import React, { useEffect, useContext, useState, useMemo, lazy } from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import useProfile from "common/hooks/useProfile";
import {
  atBqnTarget,
  validateIPv4IPv6,
  safeStr,
  getQuantileStats,
} from "common/api";
import {
  showDynPolicyRate,
  showDynPolicyRateCluster,
} from "views/Config/PolicyEdit/utils";
import FilteringControls from "./FilteringControls";
import CreateSubscriberGroupModal from "./Report/CreateSubscriberGroupModal";
import UpdateShapingValueModal from "../SubscriberGroupsEdit/Shaping/Update";
import {
  doUpdateShaping,
  validate as validateShaping,
} from "../SubscriberGroupsEdit/Shaping";
import BqnContext from "common/BqnContext";
import { Header } from "common/Header";
import MarkdownHelpModal from "common/MarkdownHelpModal";
import help from "help/viewStatusSubscriberGroups.md";
import {
  BOXPLOT_METRICS_SET,
  getParsedDataSubs,
  getSubscriberGroupTypeMap,
} from "./api";
import { getThresholds } from "../Subscriber&Groups/utils";
import { safeStrStats } from "../Subscribers/Report/TimeEvolutionMetrics/utils";
import LazyInView from "common/LazyInView";
import useHoursOfReturnView from "common/hooks/useHoursOfReturnView";
import { hoursAsPeriod } from "common/constants";

const ListSubscriberGroups = lazy(() => import("./Report/ListSubscriberGroups"));
const SubscriberGroupsChartsReport = lazy(() =>
  import("./Report/SubscriberGroupsChartsReport").then((module) => ({
    default: module.SubscriberGroupsChartsReport,
  }))
);
const Statistics = lazy(() => import("./Statistics/index"));
const ApplicationSpeed = lazy(() => import("../Subscriber&Groups/ApplicationSpeed"));

const ContextMenu = () => {
  const actions = useContext(ActionsContext);
  return (
    <ul className="context-menu header-dropdown m-r--5">
      <li>
        <a
          data-loading-effect="pulse"
          data-toggle="cardloading"
          title="Help"
          onClick={() => actions.send("open-help")}
        >
          <i className="material-icons">help_outline</i>
        </a>
      </li>
      <li>
        <a
          onClick={() => actions.send("do-load")}
          data-toggle="cardloading"
          data-loading-effect="pulse"
          title="Refresh"
        >
          <i className="material-icons">refresh</i>
        </a>
      </li>
    </ul>
  );
};

const WrapperTable = styled.div`
  padding: 0px 20px 20px;
  margin-top: -44px;
`;

const SubscriberGroupsChartsReportWrapper = styled.div`
  min-height: 1000px;
`;

const allSubscribersGroup = "all-subscribers";
const subsGroupsToPlotLimit = 50;
const subsGroupsToPlotFirst = 25;

const getAllMetricsSubscriberGroups = (ifCl, direction, bqnId, mode) =>
  ifCl.run(
    atBqnTarget(`show subscriber-group all metrics-${direction}`, mode, bqnId)
  );

const parseSubsGroupsNames = (input) => {
  const lines = input.trim("\n").split("\n");
  let names = lines.map((line) => line.replace(/\s.*$/, ""));
  if (!names.includes(allSubscribersGroup)) {
    names.unshift(allSubscribersGroup);
  }
  return names;
};

function getFilteredItems(items, types) {
  if (types.length === 0) {
    return items;
  }
  const typesSet = new Set([...types]);
  return items.filter((item) => typesSet.has(item["GROUP-TYPE"]));
}

function getParsedDataNoSubs(rows, headerMap, subsGroups, subsTypes) {
  const parsedData = rows.reduce(
    (acc, row, indexRow) => {
      const [nameGroup, ...attrs] = row.trim().split(/\s+/);
      if (nameGroup) {
        acc.names.push(nameGroup);
        const item = [nameGroup, ...attrs].reduce(
          (accItem, attr, index) => {
            const attrName = headerMap[index];
            accItem[attrName] = attr;
            if (nameGroup !== "all-subscribers") {
              if (BOXPLOT_METRICS_SET.has(attrName)) {
                if (acc[attrName]) {
                  acc[attrName].push(attr);
                } else {
                  acc[attrName] = [attr];
                }
              }
            }
            return accItem;
          },
          { "GROUP-TYPE": subsTypes[nameGroup], id: indexRow },
        );
        acc.newRows.push(item);
        if (nameGroup !== "all-subscribers") {
          acc.totalGroups += 1;
        }
      }
      return acc;
    },
    { newRows: [], totalGroups: 0, names: [] },
  );
  return parsedData;
}

const getSubsToPlot = (data, selected, types) => {
  if (selected.length === 0) {
    return [];
  }
  const typesSet = new Set([...types]);
  const dataById = data.reduce((acc, row) => {
    const id = row.id;
    acc[id] = row;
    return acc;
  }, {});
  return selected
    .map((id) => dataById[id])
    .filter((row) => {
      if(row === undefined){
        return false;
      }

      const subType = row["GROUP-TYPE"];
      return types.length === 0 || typesSet.has(subType);
    }).slice(0, 50);
};

const openSubscriberGroupEdit = (subscriberGroup, settings, type) => {
  globalNavigate("viewStatusSubscriberGroups", {
    subscriberGroup,
    edit: true,
    type,
    ...settings,
  });
};

export function openRatePolicy(name, assignment, bqnId, settings, mode, returnView = "viewStatusSubscriberGroups") {
  if (assignment === "configured") {
    globalNavigate("viewStatusSubscriberGroups", {
      policy: name,
      view: "policyRateEdit",
      returnView,
      ...settings,
    });
    return Promise.resolve()
  } else if (assignment === "assigned") {
    if (bqnId === null) {
      return showDynPolicyRate(name, true);
    } else {
      return showDynPolicyRateCluster(name, mode, bqnId);
    }
  } else {
    return offerDefineIfNotFound(name, settings);
  }
}

const offerDefineIfNotFound = (name, settings) =>
  confirmCreatePolicy().then(() => {
    globalNavigate("viewPolicyRateEdit", {
      ...settings,
      create: name,
      returnView: "viewStatusSubscriberGroups",
    });
  }).catch(() => Promise.resolve());

function confirmCreatePolicy() {
  return new Promise((resolve, reject) =>
    showModalConfirm(
      `Confirm before continue`,
      `Warning: This policy is undefined, and has been assigned to some subscribers or subscriber-groups. Those subscribers are now getting a default policy from the configured rules, while thos subscriber-groups are getting no rate limitation. If you click "Define", you can now define this policy and then the assigned subscribers and groups will get it.`,
      "warning",
      () => resolve(),
      () => reject(),
      "DEFINE",
      "RETURN",
    ),
  );
}

const applyShaping =
  (onSaveDone = doesNothing) =>
  ({ field, value, subscriberGroup }) =>
    doUpdateShaping({ subscriberGroup, direction: field, value }).then(
      onSaveDone,
    );

const SubscriberGroups = ({
  returnView,
  ipAddress,
  direction,
  timePeriod,
  metric,
  use,
  types,
  nameChanged
}) => {
  const actions = useContext(ActionsContext);
  const [groupTypes, setGroupTypes] = useState({});
  const [groupNames, setGroupNames] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [statisticsData, setStatisticsDate] = useState({});
  const [thresholds, setThresholds] = useState({});
  const [colorize, setColorize] = useState(false);
  const [isFirstSelection, setIsFirstSelection] = useState(true);
  // Opened from a view that keeps its hours, such as the dashboard, the view
  // covers the period of the same length unless it is told one.
  const hoursOfReturnView = useHoursOfReturnView(returnView);
  const [settings, setSettings] = useState({
    direction: direction || "downlink",
    timePeriod: timePeriod || hoursAsPeriod[hoursOfReturnView] || "oneDay",
    metric: metric || "averageSpeed",
    use: use || "ipAddress",
    types: types || [],
    ipAddress,
  });
  const [selected, setSelected] = useState([]);
  const [showCreateSubscriberGroupModal, setShowCreateSubscriberGroupModal] =
    useState(false);
  const [meanSorting, setMeanSorting] = useState("default");
  const [fieldsMeanMap, setFieldsMeanMap] = useState({});
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const profile = useProfile() || {};

  useEffect(() => () => actions.clear(), []);

  useEffect(() => {
    if (nameChanged) {
      doLoad();
    }
  }, [nameChanged]);

  useEffect(
    () =>
      actions.recv("do-load", () => {
        doLoad();
      }),
    [],
  );

  useEffect(
    () =>
      actions.recv("create-group", () =>
        setShowCreateSubscriberGroupModal(true),
      ),
    [],
  );

  useEffect(
    () =>
      actions.recv(
        "subscriber-group-policy-rate-clicked",
        ({ policyName, assignment }) => {
          openRatePolicy(policyName, assignment, bqnId, settings, mode, returnView).catch(
            /**err is coming from the CLI so it is safe */
            (err) => showModalError("Error", err),
          );
        },
      ),
    [settings, bqnId, mode],
  );

  useEffect(
    () =>
      actions.recv("subscriber-group-type-changed", ({ type, name }) => {
        setGroupTypes({ ...groupTypes, [name]: type });
      }),
    [],
  );

  useEffect(
    () =>
      actions.recv("subscriber-policy-rate-changed", () => {
        doLoad();
      }),
    [],
  );

  useEffect(() => {
    if (Object.keys(groupTypes).length !== 0) {
      actions.recv(
        "subscriber-group-edit-clicked",
        ({ subscriberGroup }) => {
          const type = groupTypes[subscriberGroup];
          openSubscriberGroupEdit(subscriberGroup, settings, type);
        },
        true,
      );
    }
  }, [settings, groupTypes]);

  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their responses would be
  // those of the whole cluster. The effect runs again when it does.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    doLoad();
  }, [
    settings.direction,
    settings.ipAddress,
    settings.timePeriod,
    mode,
    bqnId,
  ]);

  const subGroupsToPlot = useMemo(
    () => getSubsToPlot(tableData, selected, settings.types),
    [tableData, selected, settings.types],
  );

  const updateSelected = (selection) => {
    setIsFirstSelection(false);
    if (selection.length > subsGroupsToPlotLimit) {
      showModalInfo(
        `At most 50 groups will be plotted`,
      );
    }
    setSelected(selection);
  };

  const editSubscriberGroup = (subscriberGroup, type) => {
    globalNavigate("viewStatusSubscriberGroupsEdit", {
      returnView: "viewStatusSubscriberGroups",
      subscriberGroup,
      type,
    });
  };

  const onContextClick = (name) => {
    globalNavigate("viewStatusSubscriberGroups", {
      subscriberGroup: name,
      returnView: "viewStatusSubscriberGroups",
    });
  };

  const doLoad = () => {
    let promises = [
      getSubscriberGroupTypeMap(ifCl, bqnId, mode),
      getThresholds(mode, bqnId),
    ];
    if (settings.ipAddress) {
      const { ipAddress } = settings;
      const subscriberGroupsCommand =
        ipAddress.length === 0 || validateIPv4IPv6(ipAddress) === true
          ? `show api subscriber ${ipAddress} subscriber-group`
          : `show api subscriber-id name ${safeStr(
              settings.ipAddress,
            )} subscriber-group`;
      promises.push(
        ifCl
          .run(subscriberGroupsCommand)
          .then(parseSubsGroupsNames)
          .catch((e) => {
            return [allSubscribersGroup];
          }),
      );
    }
    let subsGroups, subsTypes, thresholds;
    Promise.all(promises)
      .then(([responseTypes, responseThresholds, responseSubsGroups]) => {
        subsTypes = responseTypes;
        subsGroups = responseSubsGroups;
        thresholds = responseThresholds;
        return getAllMetricsSubscriberGroups(
          ifCl,
          settings.direction,
          bqnId,
          mode,
        );
      })
      .then((responseAllMetrics) => {
        const [header, ...rows] = responseAllMetrics.split("\n");
        const headerAttrsResp = header.trim().split(/\s+/);
        const headerAttrs = [...headerAttrsResp, "GROUP-TYPE"];
        const headerMap = headerAttrs.reduce((acc, attr, index) => {
          acc[index] = attr;
          return acc;
        }, {});
        const boxplotMetricsIndexMap = headerAttrs.reduce(
          (acc, attr, index) => {
            if (BOXPLOT_METRICS_SET.has(attr)) {
              acc[attr] = index;
            }
            return acc;
          },
          {},
        );
        const boxplotIndexMetricsMap = headerAttrs.reduce(
          (acc, attr, index) => {
            if (BOXPLOT_METRICS_SET.has(attr)) {
              acc[index] = attr;
            }
            return acc;
          },
          {},
        );

        if (!subsGroups || subsGroups.length === 0) {
          const parsedData = getParsedDataNoSubs(
            rows,
            headerMap,
            subsGroups,
            subsTypes,
          );
          const nrSelected =
            parsedData.newRows.length < subsGroupsToPlotFirst
              ? parsedData.newRows.length
              : subsGroupsToPlotFirst;
          const firstSelection = Array.from(Array(nrSelected).keys());
          setSelected(firstSelection);
          setTableData(parsedData.newRows);
          setStatisticsDate(parsedData);
          setGroupTypes(subsTypes);
          setSettings({ ...settings, ["types"]: [] });
          setThresholds(thresholds);
          setGroupNames(parsedData.names);
        } else {
          const subGroupsSet = new Set(subsGroups);
          const parsedData = getParsedDataSubs(
            rows,
            headerMap,
            subsGroups,
            subsTypes,
          );
          const namesSet = new Set(parsedData.names);
          const newSubTypes = Object.entries(subsTypes).reduce(
            (acc, [name, type]) => {
              if (namesSet.has(name)) {
                acc[name] = type;
              }
              return acc;
            },
            {},
          );
          setStatisticsDate(parsedData);
          setGroupTypes(newSubTypes);
          setTableData(parsedData.newRows);
          const nrSelected =
            parsedData.newRows.length < subsGroupsToPlotFirst
              ? parsedData.newRows.length
              : subsGroupsToPlotFirst;
          const firstSelection = Array.from(Array(nrSelected).keys());
          setSelected(firstSelection);
          setGroupNames(parsedData.names);
        }
      })
      .catch((error) => console.error(error) || showModalError("Error", error));
  };

  const metricsQuantileData = useMemo(() => {
    return Object.entries(statisticsData).reduce((acc, [key, values]) => {
      const threshold = thresholds[key];
      const custom = threshold === "auto" ? thresholds["PERCENT-WARNING"]/100 : undefined;
      if (key !== "totalGroups" && key !== "newRows" && key !== "names") {
        const validValues = values.reduce((accValues, value) => {
          if (value !== "n/a" && value !== null) {
            accValues.push(parseFloat(value));
          }
          return accValues;
        }, []);
        const quantileData = getQuantileStats(validValues, custom);
        acc[key] = quantileData;
      }
      return acc;
    }, {});
  }, [statisticsData, thresholds]);

  const targetGroups = useMemo(() => {
    return subGroupsToPlot.map((rowData) => {
      const name = rowData["SUBSCRIBER-GROUP"];
      return safeStrStats(name);
    });
  }, [subGroupsToPlot]);

  return (
    <div className="viewStatusSubscriberGroups col-lg-12 col-md-12 col-sm-12 col-xs-12">
      <div className="card">
        <Header
          title="Subscriber Groups"
          contextMenu={<ContextMenu />}
          backButton={!!returnView}
          returnUrl={returnView}
        />
        <FilteringControls
          settings={settings}
          onChange={(settings) => setSettings(settings)}
          groupTypes={groupTypes}
          thresholds={thresholds}
          setThresholds={setThresholds}
        />
      </div>
      <UpdateShapingValueModal
        doValidate={validateShaping}
        doSave={applyShaping(doLoad)}
      />
      <SubscriberGroupsChartsReportWrapper>
        <LazyInView>
          <SubscriberGroupsChartsReport
            direction={settings.direction}
            timePeriod={settings.timePeriod}
            metric={settings.metric}
            subsGroupsToPlot={subGroupsToPlot}
            ipAddress={settings.ipAddress}
            use={settings.use}
            groupTypes={groupTypes}
            mode={mode}
            bqnId={bqnId}
            isFirstSelection={isFirstSelection}
            meanSorting={meanSorting}
            setMeanSorting={setMeanSorting}
            setFieldsMeanMap={setFieldsMeanMap}
          />
        </LazyInView>
      </SubscriberGroupsChartsReportWrapper>
      {profile && profile.dpiEnabled === true ? (
        <LazyInView>
          <ApplicationSpeed
            direction={settings.direction}
            entity={"subscriberGroups"}
            targets={targetGroups}
            isFirstSelection={isFirstSelection}
            mode={mode}
            bqnId={bqnId}
            onContextClick={onContextClick}
          />
        </LazyInView>
      ) : null}
      <LazyInView>
        <Statistics
          data={statisticsData}
          thresholds={thresholds}
          setThresholds={setThresholds}
          colorize={colorize}
          setColorize={setColorize}
          metricsQuantileData={metricsQuantileData}
        />
      </LazyInView>
      <WrapperTable className="card">
        <LazyInView>
          <ListSubscriberGroups
            onRemoved={() => doLoad()}
            tableData={getFilteredItems(tableData, settings.types)}
            colorize={colorize}
            thresholds={thresholds}
            selected={selected}
            setSelected={updateSelected}
            meanSorting={meanSorting}
            fieldsMeanMap={fieldsMeanMap}
            metricsQuantileData={metricsQuantileData}
          />
        </LazyInView>
      </WrapperTable>
      <CreateSubscriberGroupModal
        open={showCreateSubscriberGroupModal}
        onOk={(value) => editSubscriberGroup(value, "generic")}
        onClose={() => setShowCreateSubscriberGroupModal(false)}
        groupNames={groupNames}
      />
      <MarkdownHelpModal title="Subscriber Groups" source={help} />
    </div>
  );
};

export default SubscriberGroups;
